import { BusyIndicator, Portal } from "client/Components";
import { useSaveSpaceChangesBeforeSwitchDialog } from "client/Features/Workspace/SaveSpaceChangesBeforeSwitchDialog";
import { navigateToWorkspace } from "client/Features/Workspace/routing";
import { useSynchronizedRef } from "client/Hooks";
import { isNil } from "client/Misc/utils";
import { GlobalNavigationBarMode } from "client_legacy/TitleBar/GlobalNavigation/navigationItem";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useMemo,
	useState
} from "react";
import { WorkspaceSpaceNavigationHandlerOptions } from "../../TitleBar/GlobalNavigation/globalNavigationStore";

export interface ISpaceNavigationHandler {
	activate(spaceId: number): void;
	canActivate(spaceId: number, mode: GlobalNavigationBarMode): boolean;
	onPreventDeactivation(mode: GlobalNavigationBarMode): Promise<boolean>;
}

export function useSpaceNavigationHandler(
	options: WorkspaceSpaceNavigationHandlerOptions | undefined
): [
	element: ReactElement | null,
	handler: ISpaceNavigationHandler
] {
	const [outsidePageHandlerElement, outsidePageHandler] = useOutsidePageHandler();
	const [spacePageHandlerElement, spacePageHandler] = useSpacePageHandler(options);

	const handlerRef: ReadonlyRefObject<ISpaceNavigationHandler> = useSynchronizedRef(
		spacePageHandler ?? outsidePageHandler
	);

	const stableHandler = useMemo(
		(): ISpaceNavigationHandler => ({
			activate: (...args) => handlerRef.current.activate(...args),
			canActivate: (...args) => handlerRef.current.canActivate(...args),
			onPreventDeactivation: (...args) => handlerRef.current.onPreventDeactivation(...args),
		}),
		[handlerRef]
	);

	const renderElement = (): ReactElement | null => {
		if (outsidePageHandlerElement || spacePageHandlerElement) {
			return (
				<>
					{outsidePageHandlerElement}
					{spacePageHandlerElement}
				</>
			);
		}

		return null;
	};

	return [
		renderElement(),
		stableHandler
	];
}

function useOutsidePageHandler(): [
	element: ReactElement | null,
	handler: ISpaceNavigationHandler
] {
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const outsidePageHandler = useMemo(
		(): ISpaceNavigationHandler => ({
			activate: (
				spaceId
			) => {
				setIsBusy(true);
				navigateToWorkspace(spaceId);
			},
			canActivate: (
				_spaceId,
				mode
			) => {
				return mode === GlobalNavigationBarMode.Normal;
			},
			onPreventDeactivation: (mode) => {
				if (mode !== GlobalNavigationBarMode.Normal) {
					return Promise.reject("Modes other then Normal are not supported");
				}

				return Promise.resolve(true);
			}
		}),
		[]
	);

	const renderElement = (): ReactElement | null => {
		if (isBusy) {
			return (
				<Portal container={document.body}>
					<BusyIndicator />
				</Portal>
			);
		}

		return null;
	};

	return [
		renderElement(),
		outsidePageHandler
	];
}

function useSpacePageHandler(
	options: WorkspaceSpaceNavigationHandlerOptions | undefined
): [
	element: ReactElement | null,
	handler: ISpaceNavigationHandler | null
] {
	const [saveSpaceDialog, openSaveSpaceDialog] = useSaveSpaceChangesBeforeSwitchDialog();

	const showSaveSpaceChangesBeforeSwitchDialog = useCallback(
		async (options: WorkspaceSpaceNavigationHandlerOptions): Promise<boolean> => {
			const {
				saveSpaceChangesCallback,
				rollbackSpaceChangesCallback
			} = options;

			const result: TryResult<boolean> = await openSaveSpaceDialog({});

			if (!result.wasSuccessful) {
				return false;
			}

			if (result.result) {
				await saveSpaceChangesCallback();
			} else {
				rollbackSpaceChangesCallback();
			}

			return true;
		},
		[openSaveSpaceDialog]
	);

	const spacePageHandler = useMemo(
		(): ISpaceNavigationHandler | null => {
			if (isNil(options)) {
				return null;
			};

			return {
				activate: (spaceId) => options.activate(spaceId),
				canActivate: (
					spaceId,
					mode
				) => {
					if (mode !== GlobalNavigationBarMode.EditSpaceMode) {
						return true;
					}

					return options.canEnterEditModeInSpace(spaceId);
				},
				onPreventDeactivation: async (mode) => {
					let canDeactivate: boolean;

					if (mode === GlobalNavigationBarMode.EditSpaceMode) {
						canDeactivate = await showSaveSpaceChangesBeforeSwitchDialog(options);
					} else {
						canDeactivate = true;
					}

					return canDeactivate;
				}
			};
		},
		[options, showSaveSpaceChangesBeforeSwitchDialog]
	);

	return [saveSpaceDialog, spacePageHandler];
}
